import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip, Popover, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button, LinearProgress } from '@mui/material';
import AssigneeAvatarStack from '../../../shared/AssigneeAvatarStack';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updateTicketPartial, setProjectDropdownStatus } from '../../../store/ticketSlice';
import { updateGanttTicket } from '../../../store/calendarSlice';
import { updateProject } from '../../../store/projectSlice';
import { toast } from 'react-toastify';
import { 
  calculateDragPosition, 
  calculateDateFromPosition, 
  validateTaskDependencies,
  calculateTaskDuration
} from './ganttUtils';
import { 
  getPriorityColor, 
  getStatusColor, 
  getTaskTypeColor, 
  getAssigneeColor, 
  lightenColor 
} from '../calendar-view/calendarColorUtils';
import { GANTT_DEPENDENCY_TYPES, GANTT_DEPENDENCY_LABELS, GANTT_DEPENDENCY_INVERSES, normalizeDependencyType, PROJECT_STATUS, UI_LABELS } from '../../../shared/appConstants';
import ProjectStatusModal from '../ProjectStatusModal';
import { formatDateWithOptionalTime } from '../../../shared/timeUtils';
import './GanttTaskCapsule.scss';
import dependencyIcon from '../../../assets/images/dependecy_icon.svg';

const GanttTaskCapsule = ({ 
  task, 
  dates, 
  viewType, 
  onClick, 
  onTaskUpdate, 
  onDragStart,
  onDragUpdate,
  onDragEnd,
  colorIndex, 
  style,
  containerRef,
  isBeingDragged,
  isBeingResized,
  boundaryInfo,
  onBoundaryClick,
  groupByOption,
  incomingDependencies = [],
  taskByIdMap,
  projectId,
  dependenciesEnabled = false,
  activeTooltipTaskId,
  setActiveTooltipTaskId
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectDropdownsByUser } = useSelector(state => state.tickets);
  const [dragState, setDragState] = useState({
    isDragging: false,
    dragType: null,
    startX: 0,
    startY: 0,
    offsetX: 0,
    offsetY: 0,
    originalStartDate: null,
    originalEndDate: null,
    originalDuration: 0,
    originalLeft: 0,
    originalWidth: 0,
    hasMoved: false
  });
  
  const [liveResizeStyle, setLiveResizeStyle] = useState(null);
  const [isHoveringArrow, setIsHoveringArrow] = useState(false);
  const [isHoveringDeps, setIsHoveringDeps] = useState(false);
  const [isHoveringAssignee, setIsHoveringAssignee] = useState(false);
  const [depsAnchorEl, setDepsAnchorEl] = useState(null);
  const [projectDueWarningOpen, setProjectDueWarningOpen] = useState(false);
  const [dependencyWarningOpen, setDependencyWarningOpen] = useState(false);
  const [completedProjectWarningOpen, setCompletedProjectWarningOpen] = useState(false);
  const [activatingProject, setActivatingProject] = useState(false);
  const [dependencyWarningBodyKey, setDependencyWarningBodyKey] = useState('gantt.dependencyWarningBody');
  const [pendingDates, setPendingDates] = useState(null);
  const capsuleRef = useRef(null);
  const lastPositionRef = useRef(null);
  const lastDropDateRef = useRef(null);

  const outgoingDependencies = React.useMemo(() => task.dependencies || [], [task.dependencies]);
  const incomingList = React.useMemo(() => incomingDependencies || [], [incomingDependencies]);
  const projectDetails = projectDropdownsByUser?.find(project => String(project.id) === String(projectId));

  // Determine completion status and color early
  const isCompleted = task.completed || task.marked_as_completed || task.is_completed || false;
  const colorClass = isCompleted ? 'color-dark-blue' : 'color-blue';

  // Handle API call to update task dates
  const handleTaskDateUpdate = useCallback(async (newStartDate, newEndDate, originalStartDate, originalEndDate) => {
    const previousDates = {
      start_date: originalStartDate ?? task.startDate,
      due_date: originalEndDate ?? task.dueDate
    };
    const nextDates = {
      start_date: newStartDate,
      due_date: newEndDate
    };

    try {
      dispatch(updateGanttTicket({
        ticketId: task.id,
        updates: nextDates
      }));
      
      await dispatch(updateTicketPartial({
        ticketId: task.id,
        ticketData: nextDates
      })).unwrap();
      
      // Determine what changed
      const startChanged = originalStartDate !== newStartDate;
      const endChanged = originalEndDate !== newEndDate;
      
      let messageKey;
      if (startChanged && endChanged) {
        messageKey = 'gantt.taskStartAndDueDatesUpdated';
      } else if (startChanged) {
        messageKey = 'gantt.taskStartDateUpdated';
      } else if (endChanged) {
        messageKey = 'gantt.taskDueDateUpdated';
      } else {
        messageKey = 'gantt.taskDatesUpdated';
      }
      
      toast.success(t(messageKey, { 
        ticketNumber: task.ticketNumber
      }));
    } catch (error) {
      dispatch(updateGanttTicket({
        ticketId: task.id,
        updates: previousDates
      }));
      toast.error(t('gantt.taskDatesUpdateFailed', { 
        ticketNumber: task.ticketNumber,
        error: error.message || 'Unknown error'
      }));
      
      // Revert the local changes on error
      if (onTaskUpdate) {
        onTaskUpdate(task.id, previousDates.start_date, previousDates.due_date, task);
      }
    }
  }, [dispatch, task, onTaskUpdate, t]);

  const handleMouseDown = useCallback((e, dragType) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!containerRef?.current || !capsuleRef.current) return;
    
    const capsuleRect = capsuleRef.current.getBoundingClientRect();
    const containerEl = containerRef.current;
    const containerRect = containerEl.getBoundingClientRect();
    const containerStyles = getComputedStyle(containerEl);
    const paddingLeft = parseFloat(containerStyles.paddingLeft) || 0;
    
    const offsetX = e.clientX - capsuleRect.left;
    const offsetY = e.clientY - capsuleRect.top;
    const originalDuration = calculateTaskDuration(task.startDate, task.dueDate);
    
    const newDragState = {
      isDragging: false, // Don't start dragging immediately
      dragType,
      startX: e.clientX,
      startY: e.clientY,
      offsetX,
      offsetY,
      originalStartDate: task.startDate,
      originalEndDate: task.dueDate,
      originalDuration,
      // Store left in scrollable content coordinates (exclude container padding)
      originalLeft: capsuleRect.left - containerRect.left + containerEl.scrollLeft - paddingLeft,
      originalWidth: capsuleRect.width,
      hasMoved: false
    };
    
    lastPositionRef.current = null;
    lastDropDateRef.current = null;
    setDragState(newDragState);
  }, [task, containerRef]);

  const handleMouseMove = useCallback((e) => {
    if (!dragState.dragType || !containerRef?.current) return;
    
    const DRAG_THRESHOLD = 5; // pixels
    const deltaX = Math.abs(e.clientX - dragState.startX);
    const deltaY = Math.abs(e.clientY - dragState.startY);
    
    // Check if we've moved enough to start dragging
    if (!dragState.isDragging && !dragState.hasMoved && (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD)) {
      setDragState(prev => ({ ...prev, isDragging: true, hasMoved: true }));
      
      onDragStart?.({
        taskId: task.id,
        task,
        isDragging: true,
        dragType: dragState.dragType,
        colorClass,
        shadowX: e.clientX - dragState.offsetX,
        shadowY: e.clientY - dragState.offsetY,
        shadowWidth: dragState.originalWidth
      });
    }
    
    if (!dragState.isDragging) return;
    
    // Use the actual container element so drag math accounts for scroll and padding
    const currentPosition = calculateDragPosition(e.clientX, containerRef.current, dates, viewType);
    if (currentPosition) {
      lastPositionRef.current = currentPosition;
      const nextDropDate = calculateDateFromPosition(currentPosition, dates, viewType);
      if (nextDropDate) {
        lastDropDateRef.current = nextDropDate;
      }
    }
    
    if (dragState.dragType === 'move') {
      const positionForUpdate = currentPosition || lastPositionRef.current;
      if (!positionForUpdate) return;
      const dropZone = viewType === 'Week' ? positionForUpdate.columnIndex : Math.max(0, Math.min(positionForUpdate, dates.length - 1));
      
      // For week view, also pass day information for drop zone visualization
      const dropZoneInfo = viewType === 'Week' ? {
        columnIndex: positionForUpdate.columnIndex,
        dayIndex: positionForUpdate.dayIndex,
        targetDate: calculateDateFromPosition(positionForUpdate, dates, viewType)
      } : dropZone;
      
      
      onDragUpdate?.({
        taskId: task.id,
        task,
        isDragging: true,
        dragType: dragState.dragType,
        colorClass: isCompleted ? 'color-dark-blue' : 'color-blue',
        shadowX: e.clientX - dragState.offsetX,
        shadowY: e.clientY - dragState.offsetY,
        shadowWidth: dragState.originalWidth,
        dropZone: dropZoneInfo
      });
    } else if (dragState.dragType === 'resize-left') {
      const containerEl = containerRef.current;
      const containerRect = containerEl.getBoundingClientRect();
      const styles = getComputedStyle(containerEl);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const contentWidth = Math.max(0, containerEl.scrollWidth - paddingLeft - paddingRight);
      const mouseX = e.clientX - containerRect.left + containerEl.scrollLeft - paddingLeft;
      // Use pixel-based calculation for smooth resizing
      const originalRight = dragState.originalLeft + dragState.originalWidth;
      const clampedRight = Math.min(originalRight, contentWidth);
      const newLeft = Math.max(0, Math.min(mouseX, clampedRight - 50)); // Min width 50px
      const newWidth = originalRight - newLeft;
      
      setLiveResizeStyle({
        left: `${newLeft}px`,
        width: `${newWidth}px`
      });
      
      const positionForUpdate = currentPosition || lastPositionRef.current;
      if (!positionForUpdate) return;
      const dropZoneInfo = viewType === 'Week' ? {
        columnIndex: positionForUpdate.columnIndex,
        dayIndex: positionForUpdate.dayIndex,
        targetDate: calculateDateFromPosition(positionForUpdate, dates, viewType)
      } : positionForUpdate;
      
      onDragUpdate?.({
        taskId: task.id,
        task,
        isDragging: true,
        dragType: dragState.dragType,
        colorClass: isCompleted ? 'color-dark-blue' : 'color-blue',
        dropZone: dropZoneInfo
      });
    } else if (dragState.dragType === 'resize-right') {
      const containerEl = containerRef.current;
      const containerRect = containerEl.getBoundingClientRect();
      const styles = getComputedStyle(containerEl);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const contentWidth = Math.max(0, containerEl.scrollWidth - paddingLeft - paddingRight);
      const mouseX = e.clientX - containerRect.left + containerEl.scrollLeft - paddingLeft;
      // Use pixel-based calculation for smooth resizing
      const maxWidth = Math.max(50, contentWidth - dragState.originalLeft);
      const newWidth = Math.min(maxWidth, Math.max(50, mouseX - dragState.originalLeft)); // Min width 50px
      
      setLiveResizeStyle({
        width: `${newWidth}px`
      });
      
      const positionForUpdate = currentPosition || lastPositionRef.current;
      if (!positionForUpdate) return;
      const dropZoneInfo = viewType === 'Week' ? {
        columnIndex: positionForUpdate.columnIndex,
        dayIndex: positionForUpdate.dayIndex,
        targetDate: calculateDateFromPosition(positionForUpdate, dates, viewType)
      } : positionForUpdate;
      
      onDragUpdate?.({
        taskId: task.id,
        task,
        isDragging: true,
        dragType: dragState.dragType,
        colorClass: isCompleted ? 'color-dark-blue' : 'color-blue',
        dropZone: dropZoneInfo
      });
    }
  }, [dragState, dates, viewType, containerRef, task, onDragStart, onDragUpdate, colorClass, isCompleted]);

  const proceedDateUpdate = useCallback((newStartDate, newEndDate, originalStartDate, originalEndDate) => {
    const validation = validateTaskDependencies(task, newStartDate, newEndDate, []);
    if (validation.valid) {
      if (onTaskUpdate) {
        onTaskUpdate(task.id, newStartDate, newEndDate, task);
      }
      handleTaskDateUpdate(newStartDate, newEndDate, originalStartDate, originalEndDate);
    }
  }, [task, onTaskUpdate, handleTaskDateUpdate]);

  const forceDateUpdate = useCallback((newStartDate, newEndDate, originalStartDate, originalEndDate) => {
    if (onTaskUpdate) {
      onTaskUpdate(task.id, newStartDate, newEndDate);
    }
    handleTaskDateUpdate(newStartDate, newEndDate, originalStartDate, originalEndDate);
  }, [task.id, onTaskUpdate, handleTaskDateUpdate]);

  const getCompletedProjectDateRestriction = useCallback((newStartDate, newEndDate) => {
    const projectDueDate = projectDetails?.due_date ? projectDetails.due_date.split('T')[0] : null;
    if (!projectDetails || projectDetails.status !== PROJECT_STATUS.COMPLETED || !projectDueDate) return null;

    const originalStart = dragState.originalStartDate ?? task.startDate;
    const originalEnd = dragState.originalEndDate ?? task.dueDate;
    const startExceeded = newStartDate && newStartDate > projectDueDate && newStartDate !== originalStart;
    const endExceeded = newEndDate && newEndDate > projectDueDate && newEndDate !== originalEnd;

    if (!startExceeded && !endExceeded) return null;

    return {
      projectId: projectDetails.id,
      projectDueDate
    };
  }, [projectDetails, dragState.originalStartDate, dragState.originalEndDate, task.startDate, task.dueDate]);

  const runProjectDueCheckAndProceed = useCallback((newStartDate, newEndDate, originalStartDate, originalEndDate) => {
    const projectDueDateRaw = projectDropdownsByUser?.find(p => String(p.id) === String(projectId))?.due_date || null;
    if (projectDueDateRaw) {
      const projectDueDate = projectDueDateRaw.split('T')[0];
      const dueLimit = parseDateLocal(projectDueDate);
      const startCheck = parseDateLocal(newStartDate);
      const endCheck = parseDateLocal(newEndDate);
      const exceeds =
        (startCheck && dueLimit && startCheck > dueLimit) ||
        (endCheck && dueLimit && endCheck > dueLimit);
      if (exceeds) {
        setPendingDates({ newStartDate, newEndDate, projectDueDate, originalStartDate, originalEndDate });
        // Clear tooltip before showing warning modal
        setActiveTooltipTaskId?.(null);
        setProjectDueWarningOpen(true);
        return;
      }
    }
    proceedDateUpdate(newStartDate, newEndDate, originalStartDate, originalEndDate);
  }, [projectDropdownsByUser, projectId, proceedDateUpdate]);

  const handleCompletedProjectReactivate = useCallback(async () => {
    if (!projectDetails?.id || !pendingDates) return;

    setActivatingProject(true);
    dispatch(setProjectDropdownStatus({ projectId: projectDetails.id, status: PROJECT_STATUS.ACTIVE }));

    try {
      await dispatch(updateProject({
        id: projectDetails.id,
        data: { status: PROJECT_STATUS.ACTIVE }
      })).unwrap();

      setCompletedProjectWarningOpen(false);
      forceDateUpdate(pendingDates.newStartDate, pendingDates.newEndDate, pendingDates.originalStartDate, pendingDates.originalEndDate);
      setPendingDates(null);
      toast.success(t('projects.projectUpdatedSuccess'));
    } catch (error) {
      dispatch(setProjectDropdownStatus({ projectId: projectDetails.id, status: PROJECT_STATUS.COMPLETED }));
      toast.error(t('projects.projectUpdateFailed'));
    } finally {
      setActivatingProject(false);
    }
  }, [projectDetails, pendingDates, dispatch, forceDateUpdate, t]);

  const getDependencyViolation = useCallback((newStartDate, newEndDate) => {
    const newStart = parseDateLocal(newStartDate);
    const newEnd = parseDateLocal(newEndDate);
    const originalStart = parseDateLocal(dragState.originalStartDate);
    const originalEnd = parseDateLocal(dragState.originalEndDate);
    if (!newStart || !newEnd || !originalStart || !originalEnd) return { hasNewViolation: false };

    const getTargetId = (dep) => (
      dep?.targetTaskId ??
      dep?.target_task_id ??
      dep?.targetTaskID ??
      dep?.targetTask?.id ??
      dep?.target_task?.id ??
      dep?.target_id
    );

    const incomingCandidates = Array.isArray(incomingList) && incomingList.length > 0
      ? incomingList
      : (() => {
          if (!taskByIdMap?.values) return [];
          const derived = [];
          taskByIdMap.forEach((value) => {
            (value?.dependencies || []).forEach(dep => {
              const targetId = getTargetId(dep);
              if (targetId !== undefined && targetId !== null && String(targetId) === String(task.id)) {
                derived.push({ sourceTask: value, dep });
              }
            });
          });
          return derived;
        })();

    let hasNewViolation = false;
    let hasIncomingViolation = false;
    let hasOutgoingViolation = false;
    let violationCount = 0;
    const hasStartMoved = newStart.getTime() !== originalStart.getTime();

    // Check incoming dependencies (this task depends on others)
    incomingCandidates.forEach(entry => {
      const source = entry?.sourceTask;
      const sourceStart = source?.startDate ? parseDateLocal(source.startDate) : null;
      if (!sourceStart) return;
      const newViol = newStart < sourceStart;
      
      if (hasStartMoved && newViol) {
        // New violation: task moved from valid to invalid position
        hasNewViolation = true;
        hasIncomingViolation = true;
        violationCount += 1;
      }
    });

    // Check outgoing dependencies (other tasks depend on this one)
    outgoingDependencies.forEach(dep => {
      const targetId = getTargetId(dep);
      if (targetId === undefined || targetId === null) return;
      const targetTask = taskByIdMap?.get?.(String(targetId));
      const targetStart = targetTask?.startDate ? parseDateLocal(targetTask.startDate) : null;
      if (!targetStart) return;
      const newViol = newStart > targetStart;
      
      if (hasStartMoved && newViol) {
        // New violation: task moved from valid to invalid position
        hasNewViolation = true;
        hasOutgoingViolation = true;
        violationCount += 1;
      }
    });

    return { 
      hasNewViolation,
      hasIncomingViolation,
      hasOutgoingViolation,
      violationCount 
    };
  }, [incomingList, outgoingDependencies, taskByIdMap, task.id, dragState.originalStartDate, dragState.originalEndDate]);

  const resetDragState = useCallback((wasDragging = false) => {
    onDragEnd?.();
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    setDragState({
      isDragging: false,
      dragType: null,
      startX: 0,
      startY: 0,
      offsetX: 0,
      offsetY: 0,
      originalStartDate: null,
      originalEndDate: null,
      originalDuration: 0,
      originalLeft: 0,
      originalWidth: 0,
      hasMoved: wasDragging
    });
    setLiveResizeStyle(null);
    lastPositionRef.current = null;
    lastDropDateRef.current = null;

    // Reset hasMoved after delay to prevent click
    if (wasDragging) {
      setTimeout(() => {
        setDragState(prev => ({ ...prev, hasMoved: false }));
      }, 100);
    }
  }, [onDragEnd]);

  const handleMouseUp = useCallback((e) => {
    if (!dragState.dragType) return;
    
    let wasDragging = dragState.isDragging;
    
    // If we were dragging, handle the drop
    if (dragState.isDragging && containerRef?.current) {
      const currentPosition = lastPositionRef.current || calculateDragPosition(e.clientX, containerRef.current, dates, viewType);
      if (!currentPosition) {
        resetDragState(wasDragging);
        return;
      }
      
      const newDate = lastDropDateRef.current || calculateDateFromPosition(currentPosition, dates, viewType);
      
      if (newDate) {
        let newStartDate, newEndDate;
        
        if (dragState.dragType === 'resize-left') {
          newStartDate = newDate;
          newEndDate = dragState.originalEndDate;
        } else if (dragState.dragType === 'resize-right') {
          newStartDate = dragState.originalStartDate;
          newEndDate = newDate;
        } else if (dragState.dragType === 'move') {
          // For move operations, maintain the task duration
          const newStartDateObj = parseDateLocal(newDate);
          const newEndDateObj = new Date(newStartDateObj);
          newEndDateObj.setDate(newStartDateObj.getDate() + dragState.originalDuration - 1);
          
          newStartDate = `${newStartDateObj.getFullYear()}-${String(newStartDateObj.getMonth() + 1).padStart(2, '0')}-${String(newStartDateObj.getDate()).padStart(2, '0')}`;
          newEndDate = `${newEndDateObj.getFullYear()}-${String(newEndDateObj.getMonth() + 1).padStart(2, '0')}-${String(newEndDateObj.getDate()).padStart(2, '0')}`;
        }
        
        // Check if dates have actually changed
        const startDateChanged = newStartDate !== dragState.originalStartDate;
        const endDateChanged = newEndDate !== dragState.originalEndDate;
        
        if (startDateChanged || endDateChanged) {
          const completedProjectRestriction = getCompletedProjectDateRestriction(newStartDate, newEndDate);
          if (completedProjectRestriction) {
            setPendingDates({
              newStartDate,
              newEndDate,
              projectDueDate: completedProjectRestriction.projectDueDate,
              originalStartDate: dragState.originalStartDate,
              originalEndDate: dragState.originalEndDate
            });
            setActiveTooltipTaskId?.(null);
            setCompletedProjectWarningOpen(true);
            resetDragState(wasDragging);
            return;
          }

          const violation = getDependencyViolation(newStartDate, newEndDate);
          if (violation.hasNewViolation) {
            const isOutgoingOnly = violation.hasOutgoingViolation && !violation.hasIncomingViolation;
            setDependencyWarningBodyKey(
              isOutgoingOnly ? 'gantt.dependencyWarningBodySourceAfterTarget' : 'gantt.dependencyWarningBody'
            );
            setPendingDates({ newStartDate, newEndDate, originalStartDate: dragState.originalStartDate, originalEndDate: dragState.originalEndDate });
            // Clear tooltip before showing warning modal
            setActiveTooltipTaskId?.(null);
            setDependencyWarningOpen(true);
            resetDragState(wasDragging);
            return;
          }

          runProjectDueCheckAndProceed(newStartDate, newEndDate, dragState.originalStartDate, dragState.originalEndDate);
        }
        // If dates haven't changed, no need to call API or show any message
      }
      
      onDragEnd?.();
    }
    
    resetDragState(wasDragging);
  }, [dragState, dates, viewType, containerRef, getCompletedProjectDateRestriction, getDependencyViolation, runProjectDueCheckAndProceed, resetDragState, onDragEnd, setActiveTooltipTaskId]);

  useEffect(() => {
    if (dragState.dragType) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      if (dragState.isDragging) {
        document.body.style.cursor = dragState.dragType === 'move' ? 'grabbing' : 'ew-resize';
        document.body.style.userSelect = 'none';
      }
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [dragState.dragType, dragState.isDragging, handleMouseMove, handleMouseUp]);

  const handleClick = useCallback((e) => {
    // Only allow click if we haven't dragged
    if (dragState.hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setActiveTooltipTaskId?.(null);
    onClick?.(e);
  }, [dragState.hasMoved, onClick, setActiveTooltipTaskId]);

  const getAssigneeNames = useCallback(() => {
    const assignees = Array.isArray(task.assignees) && task.assignees.length > 0
      ? task.assignees
      : (task.assignee ? [task.assignee] : []);
    return assignees
      .map(a => a?.name || `${a?.first_name || ''} ${a?.last_name || ''}`.trim())
      .filter(Boolean);
  }, [task]);

  const truncateName = useCallback((name, maxLength = 30) => {
    if (!name || name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  }, []);

  const formatAssigneeLabel = useCallback((names) => {
    if (names.length === 0) return 'Unassigned';
    const maxVisible = 10;
    const visibleNames = names.slice(0, maxVisible).map(name => truncateName(name));
    const remaining = names.length - maxVisible;
    return remaining > 0 
      ? `${visibleNames.join(', ')} +${remaining}`
      : visibleNames.join(', ');
  }, [truncateName]);

  const formatTaskTooltip = useCallback(() => {
    const startDate = task.startDate
      ? new Date(task.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
      : UI_LABELS.NOT_SET;
    const dueDate = task.dueDateRaw || task.dueDate
      ? formatDateWithOptionalTime(task.dueDateRaw || task.dueDate, task.hasDueTime, { locale: 'en-US', fallback: UI_LABELS.NOT_SET })
      : UI_LABELS.NOT_SET;
    const assigneeNames = getAssigneeNames();
    const assigneeLabel = formatAssigneeLabel(assigneeNames);
    const isEpic = String(task.taskType || '').toLowerCase().includes('epic');
    const hasSubtasks = Array.isArray(task.subtasks) && task.subtasks.length > 0;
    const showProgress = isEpic || hasSubtasks;
    const calculateTaskProgress = (t) => {
      const isCompleted = t.completed || t.marked_as_completed || t.is_completed || false;
      if (!t.subtasks?.length) return isCompleted ? 100 : 0;
      const calc = (subs) => {
        let totalProgress = 0;
        let totalWeight = 0;
        subs.forEach((subtask) => {
          const subtaskCompleted = subtask.completed || subtask.marked_as_completed || subtask.is_completed || false;
          if (subtask.subtasks && subtask.subtasks.length > 0) {
            totalProgress += calc(subtask.subtasks);
          } else {
            totalProgress += subtaskCompleted ? 100 : 0;
          }
          totalWeight += 1;
        });
        return totalWeight > 0 ? totalProgress / totalWeight : 0;
      };
      return calc(t.subtasks);
    };
    const progress = showProgress ? calculateTaskProgress(task) : 0;

    return (
      <Box className="gantt-task-tooltip-content">
        <Typography className="gantt-task-tooltip-type">{task.taskType || 'Task'}</Typography>
        <Typography className="gantt-task-tooltip-title">
          <span className="gantt-task-tooltip-ticket">{task.ticketNumber}</span> {task.title}
        </Typography>
        {showProgress && (
          <LinearProgress
            variant="determinate"
            value={progress}
            className="gantt-task-tooltip-progress"
          />
        )}
        <Box className="gantt-task-tooltip-dates">
          <Box className="gantt-task-tooltip-date-col">
            <Typography className="gantt-task-tooltip-date-label">Start date</Typography>
            <Typography className="gantt-task-tooltip-date-value">{startDate}</Typography>
          </Box>
          <Box className="gantt-task-tooltip-date-col right">
            <Typography className="gantt-task-tooltip-date-label">Due Date</Typography>
            <Typography className="gantt-task-tooltip-date-value">{dueDate}</Typography>
          </Box>
        </Box>
        <Typography className="gantt-task-tooltip-assignee-label">Assignee</Typography>
        <Box className="gantt-task-tooltip-assignee-row">
          <AssigneeAvatarStack
            assignees={task.assignees}
            assignee={task.assignee}
            max={3}
            size={20}
            showTooltip={false}
            stackDirection="column"
          />
          <Typography className="gantt-task-tooltip-assignee-names">{assigneeLabel}</Typography>
        </Box>
      </Box>
    );
  }, [task, getAssigneeNames, formatAssigneeLabel]);

  const formatDateForTooltip = useCallback((dateStr) => {
    if (!dateStr) return '';
    const date = parseDateLocal(dateStr);
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  const formatDueDateForTooltip = useCallback((targetTask) => (
    formatDateWithOptionalTime(targetTask?.dueDateRaw || targetTask?.dueDate, targetTask?.hasDueTime, {
      locale: 'en-US',
      fallback: ''
    })
  ), []);

  const getTaskColor = useCallback(() => {
    if (!groupByOption) return null;

    switch (groupByOption) {
      case 'priority':
        return getPriorityColor(task.priority);
      case 'status':
        return getStatusColor(task.status);
      case 'taskType':
        return getTaskTypeColor(task.taskType);
      case 'assignee':
        return getAssigneeColor(task.assignees?.[0] || task.assignee);
      default:
        return null;
    }
  }, [task, groupByOption]);

  const renderColorIndicator = useCallback(() => {
    if (groupByOption === 'assignee') {
      const assignees = Array.isArray(task.assignees) && task.assignees.length > 0
        ? task.assignees
        : (task.assignee ? [task.assignee] : []);
      if (assignees.length === 0) return null;
      const visible = assignees.slice(0, 3);
      const extraCount = assignees.length - visible.length;
      return (
        <Box className="gantt-color-indicator-container">
          {visible.map((assignee, index) => {
            const color = getAssigneeColor(assignee);
            const lightColor = lightenColor(color, 85);
            return (
              <Box
                key={assignee?.id || assignee?.name || index}
                className="gantt-assignee-color-circle"
                style={{
                  '--gantt-circle-color': color,
                  '--gantt-circle-border': lightColor,
                  '--gantt-circle-z': visible.length - index
                }}
              />
            );
          })}
          {extraCount > 0 && (
            <Box
              key="assignee-more"
              className={`gantt-capsule-assignee-more${visible.length === 0 ? ' is-first' : ''}`}
            >
              +
            </Box>
          )}
        </Box>
      );
    }

    const color = getTaskColor();
    if (!color) return null;

    const lightColor = lightenColor(color, 85);
    
    return (
      <Box 
        className="gantt-single-color-indicator"
        style={{
          '--gantt-indicator-color': color,
          '--gantt-indicator-border': lightColor
        }}
      />
    );
  }, [groupByOption, task.assignee, task.assignees, getTaskColor]);

  // Determine color based on completion status
  const finalStyle = liveResizeStyle ? { ...style, ...liveResizeStyle } : style;
  
  // Determine boundary classes
  const boundaryClasses = [];
  if (boundaryInfo?.isIncoming) boundaryClasses.push('incoming');
  if (boundaryInfo?.isOutgoing) boundaryClasses.push('outgoing');
  
  
  const parseDateLocal = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const formatRelationshipLabel = (value) => {
    const normalized = normalizeDependencyType(value);
    if (!normalized) return GANTT_DEPENDENCY_TYPES.BLOCKS;
    if (normalized === 'blocks') return GANTT_DEPENDENCY_TYPES.BLOCKS;
    if (normalized === 'required for') return GANTT_DEPENDENCY_TYPES.REQUIRED_FOR;
    if (normalized === 'dependent on' || normalized === 'depends on') return GANTT_DEPENDENCY_LABELS.DEPENDENT_ON;
    return normalized
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const invertRelationshipLabel = (value) => {
    const label = formatRelationshipLabel(value);
    return GANTT_DEPENDENCY_INVERSES[label] || label;
  };

  const outgoingHidden = Math.max(0, outgoingDependencies.length - 2);
  const incomingHidden = Math.max(0, incomingList.length - 2);
  const showDependencyIcon = dependenciesEnabled && (outgoingDependencies.length > 2 || incomingList.length > 2);
  const depsTooltip = (
    <Box>
      {outgoingHidden > 0 && (
        <Typography variant="body2">{`+${outgoingHidden} more outgoing dependencies`}</Typography>
      )}
      {incomingHidden > 0 && (
        <Typography variant="body2">{`+${incomingHidden} more incoming dependencies`}</Typography>
      )}
    </Box>
  );

  const handleDepsOpen = (e) => {
    e.stopPropagation();
    setDepsAnchorEl(e.currentTarget);
  };

  const handleDepsClose = () => {
    setDepsAnchorEl(null);
  };

  const depsOpen = Boolean(depsAnchorEl);
  const depsId = depsOpen ? `deps-popover-${task.id}` : undefined;
  const isTooltipBlocked = isHoveringArrow || isHoveringDeps || isHoveringAssignee || isBeingDragged || isBeingResized || dragState.isDragging || projectDueWarningOpen || dependencyWarningOpen;
  const isTooltipOpen = activeTooltipTaskId === task.id && !isTooltipBlocked;

  return (
    <>
      <Tooltip
        title={(isHoveringArrow || isHoveringDeps || isHoveringAssignee) ? '' : formatTaskTooltip()}
        placement="bottom-start"
        classes={{ tooltip: 'gantt-task-tooltip gantt-task-tooltip-capsule' }}
        open={isTooltipOpen}
        disableFocusListener
        disableTouchListener
        disableInteractive
        PopperProps={{
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: containerRef?.current || 'viewport',
                altBoundary: true,
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
              },
            },
          ],
        }}
      >
        <Box
          ref={capsuleRef}
          data-task-id={task.id}
          className={`gantt-task-capsule ${colorClass} ${isBeingDragged ? 'being-dragged' : ''} ${isBeingResized ? 'being-resized' : ''} ${boundaryClasses.join(' ')}`}
          onClick={handleClick}
          onMouseEnter={() => setActiveTooltipTaskId?.(task.id)}
          onMouseLeave={() => setActiveTooltipTaskId?.(null)}
          style={finalStyle}
        >
    {/* Incoming arrow for boundary tasks */}
    {boundaryInfo?.isIncoming && (
      <Tooltip 
        title={`Start Date: ${formatDateForTooltip(task.startDate)}`}
        placement="top"
        arrow
        classes={{ tooltip: 'app-tooltip' }}
      >
        <IconButton 
          className="gantt-boundary-arrow incoming-arrow"
          size="small"
          onMouseEnter={() => setIsHoveringArrow(true)}
          onMouseLeave={() => setIsHoveringArrow(false)}
          onClick={(e) => {
            e.stopPropagation();
            onBoundaryClick?.(boundaryInfo, 'prev', task.id);
          }}
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
      
      <Box 
        className="gantt-task-capsule-resize-handle left"
        onMouseDown={(e) => handleMouseDown(e, 'resize-left')}
      />
      
      <Box 
        className="gantt-task-capsule-content"
        onMouseDown={(e) => handleMouseDown(e, 'move')}
      >
        <Box className="gantt-capsule-text-container">
          {renderColorIndicator()}
          <Typography className="gantt-task-capsule-text" variant="body2">
            {task.ticketNumber} {task.title}
          </Typography>
        </Box>
        <Box className="gantt-capsule-icons">
          {showDependencyIcon && (
            <Tooltip
              title={depsTooltip}
              placement="top"
              arrow
              classes={{ tooltip: 'app-tooltip' }}
            >
              <IconButton
                className="gantt-task-capsule-deps"
                size="small"
                onClick={handleDepsOpen}
                onMouseEnter={() => setIsHoveringDeps(true)}
                onMouseLeave={() => setIsHoveringDeps(false)}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <img src={dependencyIcon} alt="Dependencies" className="gantt-task-capsule-deps-icon" />
              </IconButton>
            </Tooltip>
          )}
          {(task.assignees?.length || task.assignee) && (
            <Box
              className="gantt-task-capsule-avatar-stack"
              onMouseEnter={(e) => {
                e.stopPropagation();
                setIsHoveringAssignee(true);
              }}
              onMouseLeave={(e) => {
                e.stopPropagation();
                setIsHoveringAssignee(false);
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <AssigneeAvatarStack
                assignees={task.assignees}
                assignee={task.assignee}
                max={2}
                size={22}
                colorResolver={groupByOption === 'assignee' ? getAssigneeColor : null}
                showTooltip={false}
              />
            </Box>
          )}
        </Box>
      </Box>
      
      <Box 
        className="gantt-task-capsule-resize-handle right"
        onMouseDown={(e) => handleMouseDown(e, 'resize-right')}
      />
      
      {/* Outgoing arrow for boundary tasks */}
      {boundaryInfo?.isOutgoing && (
        <Tooltip 
          title={`Due Date: ${formatDueDateForTooltip(task)}`}
          placement="top"
          arrow
          classes={{ tooltip: 'app-tooltip' }}
        >
          <IconButton 
            className="gantt-boundary-arrow outgoing-arrow"
            size="small"
            onMouseEnter={() => setIsHoveringArrow(true)}
            onMouseLeave={() => setIsHoveringArrow(false)}
            onClick={(e) => {
              e.stopPropagation();
              onBoundaryClick?.(boundaryInfo, 'next', task.id);
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
        </Box>
      </Tooltip>
      <Popover
        id={depsId}
        open={depsOpen}
        anchorEl={depsAnchorEl}
        onClose={handleDepsClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: 'gantt-deps-popover' }}
      >
        <Box className="gantt-deps-popover-content">
          <Typography className="gantt-deps-title">Outgoing</Typography>
          {outgoingDependencies.length === 0 ? (
            <Typography className="gantt-deps-empty">No outgoing dependencies</Typography>
          ) : (
            outgoingDependencies.map((dep, idx) => {
              const targetTask = taskByIdMap?.get?.(String(dep.targetTaskId));
              const targetNumber =
                dep.targetTicketNumber ||
                dep.targetTaskLabel ||
                targetTask?.ticketNumber ||
                targetTask?.ticket_number ||
                dep.targetTaskId;
              const label = formatRelationshipLabel(dep.relationshipType);
              return (
                <Typography key={`out-${task.id}-${idx}`} className="gantt-deps-item">
                  {task.ticketNumber || task.id}{' '}
                  <span className="gantt-deps-rel" style={{ '--gantt-dep-color': dep.colour || dep.color }}>
                    {label}
                  </span>{' '}
                  {targetNumber}
                </Typography>
              );
            })
          )}
          <Divider className="gantt-deps-divider" />
          <Typography className="gantt-deps-title">Incoming</Typography>
          {incomingList.length === 0 ? (
            <Typography className="gantt-deps-empty">No incoming dependencies</Typography>
          ) : (
            incomingList.map((entry, idx) => {
              const source = entry.sourceTask || {};
              const sourceNumber = source.ticketNumber || source.ticket_number || source.id;
              const label = invertRelationshipLabel(entry.dep?.relationshipType);
              return (
                <Typography key={`in-${task.id}-${idx}`} className="gantt-deps-item">
                  {task.ticketNumber || task.id}{' '}
                  <span className="gantt-deps-rel" style={{ '--gantt-dep-color': entry.dep?.colour || entry.dep?.color }}>
                    {label}
                  </span>{' '}
                  {sourceNumber}
                </Typography>
              );
            })
          )}
        </Box>
      </Popover>
      <Dialog
        open={projectDueWarningOpen}
        onClose={() => {
          setProjectDueWarningOpen(false);
          setPendingDates(null);
        }}
        PaperProps={{ className: 'gantt-project-due-dialog' }}
      >
        <DialogTitle className="gantt-project-due-title">
          {t('gantt.projectDueDateWarningTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="gantt-project-due-body">
            {t('gantt.projectDueDateWarningBody', { date: pendingDates?.projectDueDate })}
          </Typography>
        </DialogContent>
        <DialogActions className="gantt-project-due-actions">
          <Button
            onClick={() => {
              setProjectDueWarningOpen(false);
              setPendingDates(null);
            }}
            className="gantt-project-due-cancel"
          >
            {t('gantt.projectDueDateWarningCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const next = pendingDates;
              setProjectDueWarningOpen(false);
              setPendingDates(null);
              if (next) {
                proceedDateUpdate(next.newStartDate, next.newEndDate, next.originalStartDate, next.originalEndDate);
              }
            }}
            className="gantt-project-due-continue"
          >
            {t('gantt.projectDueDateWarningContinue')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={dependencyWarningOpen}
        onClose={() => {
          setDependencyWarningOpen(false);
          setPendingDates(null);
        }}
        PaperProps={{ className: 'gantt-dependency-warning-dialog' }}
      >
        <DialogTitle className="gantt-dependency-warning-title">
          {t('gantt.dependencyWarningTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="gantt-dependency-warning-body">
            {t(dependencyWarningBodyKey)}
          </Typography>
        </DialogContent>
        <DialogActions className="gantt-dependency-warning-actions">
          <Button
            onClick={() => {
              setDependencyWarningOpen(false);
              setPendingDates(null);
            }}
            className="gantt-dependency-warning-cancel"
          >
            {t('gantt.dependencyWarningCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const next = pendingDates;
              setDependencyWarningOpen(false);
              setPendingDates(null);
              if (next) {
                runProjectDueCheckAndProceed(next.newStartDate, next.newEndDate, next.originalStartDate, next.originalEndDate);
              }
            }}
            className="gantt-dependency-warning-continue"
          >
            {t('gantt.dependencyWarningContinue')}
          </Button>
        </DialogActions>
      </Dialog>
      <ProjectStatusModal
        open={completedProjectWarningOpen}
        onClose={() => {
          setCompletedProjectWarningOpen(false);
          setPendingDates(null);
        }}
        onActivate={handleCompletedProjectReactivate}
        loading={activatingProject}
        projectStatus={PROJECT_STATUS.COMPLETED}
        contentOverride={{
          icon: 'CheckCircle',
          colorClass: 'info',
          title: t('projects.completedProjectDateLimitTitle'),
          message: t('projects.completedProjectDateLimitMessage', { date: pendingDates?.projectDueDate || projectDetails?.due_date?.split('T')[0] || '' }),
          actionLabel: t('projects.reactivateAndContinue'),
          loadingLabel: t('projects.reactivatingAndContinuing')
        }}
      />
    </>
  );
};

export default GanttTaskCapsule;
